import { Panels } from '@gui/constants/Panels';
import usePersistentStore, { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { Box, Tooltip } from '@mui/material';
import { resolveCoordinateSystemSelection } from '@data/geodata/CoordinateSystemUtils';
import useNotificationStore from '@gui/store/notification';
import { UMCoordinateSystemSelector } from '@urbanmetrix/ui-kit';
import type { CoordinateSystemSelection } from '@visualizer/types';
import React from 'react';
import { NotificationType } from '@visualizer/types';
import { useResourceStore } from 'store/ResourceStore';
import { ApiRoutes } from 'store/ApiRoutes';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';
import { updateCoordinateReferenceSystem } from '@api/gridApi';
import CustomTooltip from './CustomTooltip';
import { InfoCircle } from '@untitledui-pro/icons/line';
import { useCapabilities } from '../../../demo/capabilities';

type CoordinateSystemSelectorControlProps = {
    title?: React.ReactNode;
    fontSize?: number;
    layout?: 'row' | 'column';
};

const lastPersistedSelectionByGridId = new Map<string, CoordinateSystemSelection>();

const areSelectionsEqual = (a: CoordinateSystemSelection, b: CoordinateSystemSelection): boolean =>
    a.mode === b.mode && a.centerX === b.centerX && a.centerY === b.centerY && a.centerZ === b.centerZ && a.alpha === b.alpha;

const hasCustomCoordinates = (selection: CoordinateSystemSelection): boolean =>
    selection.centerX !== 0 || selection.centerY !== 0 || selection.centerZ !== 0 || selection.alpha !== 0;

const CoordinateSystemSelectorControl: React.FC<CoordinateSystemSelectorControlProps> = ({
    title = 'Coordinate System',
    fontSize = 8,
    layout = 'column',
}) => {
    const CoordinateSystemHelpTooltip = React.useMemo(
        () =>
            function CoordinateSystemHelpTooltip({ title, children }: { title: React.ReactNode; children: React.ReactElement }) {
                return (
                    <CustomTooltip side="right" title={title}>
                        {children}
                    </CustomTooltip>
                );
            },
        []
    );

    const setPanel = usePersistentStore((s) => s.setPanel);
    const coordinateSystem = useGlobalBucket((s) => s.settings.coordinateSystem);
    const gridId = useResourceStore((s) => s.gridId);
    const showNotification = useNotificationStore((s) => s.showNotification);
    const resolvedSelection = React.useMemo(() => resolveCoordinateSystemSelection(coordinateSystem), [coordinateSystem]);
    const lastValidationWasInvalidRef = React.useRef(false);

    React.useEffect(() => {
        if (!gridId || lastPersistedSelectionByGridId.has(gridId)) return;
        lastPersistedSelectionByGridId.set(gridId, resolvedSelection);
    }, [gridId, resolvedSelection]);

    const { canSwitchCoordinateSystem } = useCapabilities();
    const env = useEnvStore((s) => s.env);

    const persistCoordinateSystem = React.useCallback(
        async (value: CoordinateSystemSelection) => {
            if (!ApiRoutes.canUseApi() || !gridId) return;

            try {
                await updateCoordinateReferenceSystem(gridId, value);
                lastPersistedSelectionByGridId.set(gridId, value);
            } catch (error) {
                console.error('Could not update coordinate reference system.', error);
                showNotification('Could not save coordinate system.', NotificationType.Error);
            }
        },
        [gridId, showNotification]
    );

    const handleChange = React.useCallback(
        (value: CoordinateSystemSelection) => {
            const currentSelection = resolveCoordinateSystemSelection(usePersistentStore.getState().global.settings.coordinateSystem);
            const preserveCustomCoordinates = hasCustomCoordinates(currentSelection);
            let nextSelection = value;

            if (value.mode === 'lv95' && currentSelection.mode === 'custom' && preserveCustomCoordinates) {
                nextSelection = {
                    ...currentSelection,
                    mode: 'lv95',
                };
            }

            if (value.mode === 'custom' && !hasCustomCoordinates(value) && preserveCustomCoordinates) {
                nextSelection = {
                    ...currentSelection,
                    mode: 'custom',
                };
            }

            setPanel({ coordinateSystem: nextSelection }, Panels.SETTINGS);
        },
        [setPanel]
    );

    // Persists ONLY if the coordinates are valid (based on the validation that is done by the component):
    const handleValidationChange = React.useCallback(
        (validation: { isValid: boolean }) => {
            const currentSelection = resolveCoordinateSystemSelection(usePersistentStore.getState().global.settings.coordinateSystem);

            if (!validation.isValid) {
                if (currentSelection.mode === 'custom' && !lastValidationWasInvalidRef.current) {
                    showNotification('Coordinate system was not saved because the custom coordinates are invalid.', NotificationType.Error);
                }
                lastValidationWasInvalidRef.current = true;
                return;
            }

            lastValidationWasInvalidRef.current = false;

            if (!gridId) return;

            // Do not persist if the valid selection is same as last one:
            const lastPersistedSelection = lastPersistedSelectionByGridId.get(gridId);
            if (lastPersistedSelection && areSelectionsEqual(lastPersistedSelection, currentSelection)) return;

            void persistCoordinateSystem(currentSelection);
        },
        [gridId, persistCoordinateSystem, showNotification]
    );

    return (
        <Tooltip title={!canSwitchCoordinateSystem ? getDisabledInEnvMessage(env) : ''}>
            <Box sx={{ width: '100%', opacity: canSwitchCoordinateSystem ? 1 : 0.5 }}>
                <Box sx={{ pointerEvents: canSwitchCoordinateSystem ? 'auto' : 'none' }}>
                    <UMCoordinateSystemSelector
                        fontSize={fontSize}
                        labelsFontSize={11.43}
                        titleFontWeight="bold"
                        helpIcon={<InfoCircle className="icon-size-small" />}
                        title={title}
                        layout={layout}
                        helpTooltipComponent={CoordinateSystemHelpTooltip}
                        value={resolvedSelection}
                        onChange={handleChange}
                        onValidationChange={handleValidationChange}
                    />
                </Box>
            </Box>
        </Tooltip>
    );
};

export default CoordinateSystemSelectorControl;
